import { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  HandThumbDownIcon,
  PencilSquareIcon,
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
} from '@heroicons/react/24/outline';
import { AdminUser, CallOutcome, QueueItem, VisiteDetail } from './types';
import {
  claimLead,
  getErrorMessage,
  getQueue,
  getVisiteDetail,
  logOutcome,
  updateContact,
} from './adminApi';
import { CallActivityList } from './CallActivityList';
import { StatusBadge, formatShortDateTime } from './crmShared';

// Local date/time inputs → ISO string for the API.
function toIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString();
}

function OriginBadge({ source }: { source: string }) {
  return source === 'meta_native' ? (
    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">Meta Lead</span>
  ) : (
    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">Site web</span>
  );
}

export default function CallQueuePage({ user }: { user: AdminUser | null }) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [counts, setCounts] = useState({ total: 0, rappelsDus: 0, nouveaux: 0, relances: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [detail, setDetail] = useState<VisiteDetail | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const [activeOutcome, setActiveOutcome] = useState<CallOutcome | null>(null);
  const [note, setNote] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const [rdvDate, setRdvDate] = useState('');
  const [rdvTime, setRdvTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  const current = items[0] ?? null;

  const resetCallState = () => {
    setClaimed(false);
    setActiveOutcome(null);
    setNote('');
    setCallbackDate('');
    setCallbackTime('');
    setRdvDate('');
    setRdvTime('');
    setEditing(false);
  };

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getQueue(20, 0);
      setItems(res.items);
      setCounts(res.counts);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Load history whenever the current lead changes.
  useEffect(() => {
    resetCallState();
    if (!current) {
      setDetail(null);
      return;
    }
    let mounted = true;
    getVisiteDetail(current.id)
      .then((d) => { if (mounted) setDetail(d); })
      .catch(() => { if (mounted) setDetail(null); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const handleCall = async () => {
    if (!current) return;
    setClaiming(true);
    setNotice('');
    const phone = current.phone;
    try {
      await claimLead(current.id);
      setClaimed(true);
      // Open the dialer only once the soft-lock is secured, so we never call a
      // lead another conseiller just took.
      window.location.href = `tel:${phone}`;
    } catch (err) {
      // Someone else grabbed it (409) — skip to the next lead.
      setNotice(getErrorMessage(err));
      await loadQueue();
    } finally {
      setClaiming(false);
    }
  };

  const submitOutcome = async (outcome: CallOutcome) => {
    if (!current) return;
    setError('');

    const payload: Parameters<typeof logOutcome>[1] = { outcome };
    if (note.trim()) payload.note = note.trim();

    if (outcome === 'a_rappeler') {
      if (!callbackDate) { setError('Choisissez une date de rappel.'); return; }
      const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
      if (callbackDate < todayStr) { setError('La date de rappel ne peut pas être dans le passé.'); return; }
      // Time is optional — default to the morning (09:00) when left blank.
      payload.callbackAt = toIso(callbackDate, callbackTime || '09:00');
    } else if (outcome === 'rdv_pris') {
      if (!rdvDate || !rdvTime) { setError('Choisissez une date et une heure de RDV.'); return; }
      payload.rdvDate = rdvDate;
      payload.rdvHeure = rdvTime;
    }

    setSubmitting(true);
    try {
      await logOutcome(current.id, payload);
      setNotice('Résultat enregistré.');
      // Drop the lead from the local list and advance.
      setItems((prev) => prev.filter((it) => it.id !== current.id));
      setCounts((c) => ({ ...c, total: Math.max(0, c.total - 1) }));
      resetCallState();
      // Refresh from server to recompute ordering/counts and pull more leads.
      await loadQueue();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = () => {
    if (!current) return;
    setEditName(current.parentName);
    setEditPhone(current.phone);
    setEditing(true);
  };

  const saveContact = async () => {
    if (!current) return;
    setSavingContact(true);
    setError('');
    try {
      const updated = await updateContact(current.id, { parentName: editName, phone: editPhone });
      setItems((prev) => prev.map((it) => (it.id === current.id ? { ...it, parentName: updated.parentName, phone: updated.phone } : it)));
      setEditing(false);
      const d = await getVisiteDetail(current.id).catch(() => null);
      if (d) setDetail(d);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-950">Suivi des appels</h2>
          <p className="text-sm text-gray-500">
            {user?.name ? `Bonjour ${user.name}. ` : ''}File partagée des demandes de visite.
            {user?.role === 'TELECONSEILLER' && (
              <span className="ml-1 text-gray-400">
                · {user.niveaux.length ? `Niveaux : ${user.niveaux.join(', ')}` : 'Tous les niveaux'}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={loadQueue}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
          Actualiser
        </button>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        {[
          { label: 'Rappels dus', value: counts.rappelsDus, cls: 'text-amber-700' },
          { label: 'Nouveaux', value: counts.nouveaux, cls: 'text-emerald-700' },
          { label: 'Relances', value: counts.relances, cls: 'text-gray-700' },
        ].map(({ label, value, cls }) => (
          <div key={label} className="rounded-lg border border-gray-200 bg-white p-3 text-center shadow-sm">
            <p className={`text-xl font-bold ${cls}`}>{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {notice && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500 shadow-sm">Chargement…</div>
      ) : !current ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-emerald-500" aria-hidden="true" />
          <p className="mt-3 text-lg font-semibold text-gray-900">File vide 🎉</p>
          <p className="mt-1 text-sm text-gray-500">Aucun lead à appeler pour le moment.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <OriginBadge source={current.source} />
              <StatusBadge status={current.status} />
              {current.noAnswerCount > 0 && (
                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                  {current.noAnswerCount} tentative{current.noAnswerCount > 1 ? 's' : ''}
                </span>
              )}
              {current.nextCallbackAt && (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                  Rappel : {formatShortDateTime(current.nextCallbackAt)}
                </span>
              )}
            </div>

            {editing ? (
              <div className="space-y-2">
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Nom du parent"
                />
                <input
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Téléphone"
                  inputMode="tel"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={saveContact}
                    disabled={savingContact}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
                  >
                    {savingContact ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="break-words text-xl font-bold text-gray-950">{current.parentName}</h3>
                  <button
                    type="button"
                    onClick={startEditing}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    Corriger
                  </button>
                </div>
                <p className="mt-0.5 text-sm text-gray-600">
                  {current.niveau}
                  {current.quartier ? ` · ${current.quartier}` : ''}
                  {current.nombreEnfants > 1 ? ` · ${current.nombreEnfants} enfants` : ''}
                </p>
                {current.email && <p className="mt-0.5 text-sm text-gray-500">{current.email}</p>}
              </>
            )}
          </div>

          {/* Phone + call button */}
          <div className="px-5 py-4">
            <button
              type="button"
              onClick={handleCall}
              disabled={claiming}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-emerald-600 px-4 py-4 text-lg font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-70"
            >
              <PhoneArrowUpRightIcon className="h-6 w-6" aria-hidden="true" />
              {claiming ? 'Connexion…' : `Appeler ${current.phone}`}
            </button>
            {claimed && (
              <p className="mt-2 text-center text-xs font-medium text-emerald-700">Lead réservé pour vous — enregistrez le résultat ci-dessous.</p>
            )}
            {current.message && (
              <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-sm italic text-gray-700">« {current.message} »</p>
            )}
          </div>

          {/* Outcome actions (revealed once the lead is claimed) */}
          <div className="border-t border-gray-100 px-5 py-4">
            {!claimed ? (
              <p className="text-center text-sm text-gray-500">Cliquez sur « Appeler » pour réserver ce lead et noter le résultat.</p>
            ) : (
              <>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Note (optionnel)"
                  className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveOutcome(activeOutcome === 'rdv_pris' ? null : 'rdv_pris')}
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${
                      activeOutcome === 'rdv_pris' ? 'border-blue-600 bg-blue-600 text-white' : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
                    RDV pris
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveOutcome(activeOutcome === 'a_rappeler' ? null : 'a_rappeler')}
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${
                      activeOutcome === 'a_rappeler' ? 'border-amber-600 bg-amber-600 text-white' : 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    }`}
                  >
                    <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
                    À rappeler
                  </button>
                  <button
                    type="button"
                    onClick={() => submitOutcome('no_answer')}
                    disabled={submitting}
                    className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                  >
                    <PhoneXMarkIcon className="h-5 w-5" aria-hidden="true" />
                    Pas de réponse
                  </button>
                  <button
                    type="button"
                    onClick={() => submitOutcome('pas_interesse')}
                    disabled={submitting}
                    className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                  >
                    <HandThumbDownIcon className="h-5 w-5" aria-hidden="true" />
                    Pas intéressé
                  </button>
                </div>

                {activeOutcome === 'rdv_pris' && (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
                    <p className="mb-2 text-sm font-semibold text-blue-800">Date et heure du rendez-vous</p>
                    <div className="flex gap-2">
                      <input type="date" value={rdvDate} onChange={(e) => setRdvDate(e.target.value)} className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm" />
                      <input type="time" value={rdvTime} onChange={(e) => setRdvTime(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <button
                      type="button"
                      onClick={() => submitOutcome('rdv_pris')}
                      disabled={submitting}
                      className="mt-3 w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {submitting ? 'Enregistrement…' : 'Valider le RDV'}
                    </button>
                  </div>
                )}

                {activeOutcome === 'a_rappeler' && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                    <p className="mb-2 text-sm font-semibold text-amber-800">Quand rappeler ?</p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="mb-1 block text-xs font-medium text-amber-800">Jour</label>
                        <input
                          type="date"
                          value={callbackDate}
                          min={new Date().toLocaleDateString('en-CA')}
                          onChange={(e) => setCallbackDate(e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Heure (optionnel)</label>
                        <input type="time" value={callbackTime} onChange={(e) => setCallbackTime(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => submitOutcome('a_rappeler')}
                      disabled={submitting}
                      className="mt-3 w-full rounded-md bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                    >
                      {submitting ? 'Enregistrement…' : 'Planifier le rappel'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* History */}
          {detail && detail.callActivities.length > 0 && (
            <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Historique</p>
              <CallActivityList activities={detail.callActivities} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
